import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, absoluteUrl } from "../constants/seo";

export const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncateText = (value = "", max = 160) => {
  const text = stripHtml(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
};

export const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/logo.png`,
  description:
    "India's first recruitment portal exclusive for CA, CS, and CMA professionals.",
  foundingDate: "2013",
  areaServed: "IN",
  sameAs: [
    "https://www.facebook.com/cajobportal",
    "https://www.linkedin.com/in/cajobportal/",
    "https://twitter.com/cajobportal",
    "https://blog.cajobportal.com/",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    url: `${SITE_URL}/contact`,
    availableLanguage: ["English", "Hindi"],
  },
});

export const buildWebSiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: "en-IN",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/jobs?keyword={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const buildBreadcrumbJsonLd = (crumbs = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: absoluteUrl(crumb.path),
  })),
});

export const buildItemListJsonLd = (jobs = []) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: jobs.slice(0, 12).map((job, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: absoluteUrl(`/job-description/${job._id || job.id}`),
    name: job.title,
  })),
});

export const buildJobPostingJsonLd = (job) => {
  if (!job?._id && !job?.id) return null;

  const fromLac = Number(job?.ctc?.from_lac);
  const toLac = Number(job?.ctc?.to_lac);
  const hasSalary =
    Number.isFinite(fromLac) &&
    Number.isFinite(toLac) &&
    (fromLac > 0 || toLac > 0);

  const locationName = [job?.city, job?.state, job?.country]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter(Boolean)
    .join(", ");

  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: stripHtml(job.description || job.about_company || job.title),
    datePosted: job.admin_approval_date || job.createdAt || job.updatedAt,
    hiringOrganization: {
      "@type": "Organization",
      name: job.company_name || SITE_NAME,
    },
    identifier: {
      "@type": "PropertyValue",
      name: SITE_NAME,
      value: String(job._id || job.id),
    },
    url: absoluteUrl(`/job-description/${job._id || job.id}`),
    industry: job?.industry?.name,
    occupationalCategory: job?.qualification?.map((item) => item.name).filter(Boolean).join(", "),
    image: DEFAULT_OG_IMAGE,
  };

  if (locationName) {
    schema.jobLocation = {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: Array.isArray(job?.city) ? job.city[0] : job?.city,
        addressRegion: job?.state,
        addressCountry: job?.country || "IN",
      },
    };
  } else {
    schema.jobLocationType = "TELECOMMUTE";
    schema.applicantLocationRequirements = {
      "@type": "Country",
      name: "India",
    };
  }

  if (hasSalary) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: "INR",
      value: {
        "@type": "QuantitativeValue",
        minValue: fromLac * 100000,
        maxValue: toLac * 100000,
        unitText: "YEAR",
      },
    };
  }

  if (job?.exp?.from != null || job?.exp?.to != null) {
    schema.experienceRequirements = {
      "@type": "OccupationalExperienceRequirements",
      monthsOfExperience: Number(job?.exp?.from || 0) * 12,
    };
  }

  if (job?.job_type?.name || job?.type) {
    schema.employmentType = job?.job_type?.name || job?.type;
  }

  return schema;
};
